import { useState } from 'react';
import { ChevronDown, Clock, Layout, Tag } from 'lucide-react';
import { cn } from '@/utils/cn';

export interface CourseCardProps {
  title: string;
  duration: string;
  modules: number;
  price: string;
  description?: string;
  topics?: string[];
  delay?: number;
  index?: number;
}

const CourseCard = ({ title, duration, modules, price, description, topics, delay = 0, index }: CourseCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div 
      className={cn(
        "bg-white rounded-2xl shadow-sm overflow-hidden",
        "transform transition-all duration-500",
        "hover:shadow-lg hover:scale-[1.02]"
      )}
      style={{ 
        transitionDelay: `${delay}s`,
      }}
    >
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full text-left p-6 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-dark font-bold">
              {(index ?? 0) + 1}
            </span>
            <h3 className="text-xl font-semibold text-dark">{title}</h3>
          </div>
          <div className="flex items-center gap-6 mt-2 text-gray-600 ml-11">
            <span className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              {duration}
            </span>
            <span className="flex items-center gap-2">
              <Layout className="h-4 w-4" />
              {modules} lekcje
            </span>
            <span className="flex items-center gap-2 text-primary font-semibold line-through">
              <Tag className="h-4 w-4" />
              {price}
            </span>
          </div>
        </div>
        <ChevronDown 
          className={cn(
            "h-6 w-6 text-gray-400 transition-transform duration-300",
            isExpanded && "transform rotate-180"
          )} 
        />
      </button>

      <div 
        className={cn(
          "grid transition-all duration-300 ease-in-out",
          isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        )}
      >
        <div className="overflow-hidden">
          <div className={cn(
            "px-6 pb-6 transform transition-all duration-300",
            isExpanded ? "translate-y-0 opacity-100" : "-translate-y-4 opacity-0"
          )}>
            {description && (
              <p className="text-gray-600 mb-4 ml-11">{description}</p>
            )}
            {topics && topics.length > 0 && (
              <div className="ml-11">
                <h4 className="font-semibold text-dark mb-2">Tematy kursu:</h4>
                <ul className="space-y-2 text-gray-600">
                  {topics.map((topic, index) => (
                    <li 
                      key={index} 
                      className={cn(
                        "flex items-center gap-2 transform transition-all duration-300",
                        isExpanded ? "translate-x-0 opacity-100" : "-translate-x-4 opacity-0"
                      )}
                      style={{ transitionDelay: `${index * 0.1}s` }}
                    >
                      <div className="h-1.5 w-1.5 rounded-full bg-[#FFE81A]" />
                      {topic}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseCard;